import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Clock } from "lucide-react";
import { getAllNoticesPortal } from "../../api/noticePortalApi";
import { getAllNotices } from "../../api/noticeApi";
import { Badge } from "../ui";
import WidgetCard from "../widget/WidgetCard";

const CATEGORY_VARIANTS = {
  GENERAL: "default",
  EXAM: "primary",
  HOLIDAY: "danger",
  EVENT: "purple",
  FEE: "warning",
  URGENT: "danger",
};

export const NoticeWidget = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = isPortal ? await getAllNoticesPortal() : await getAllNotices();
        const dataList = Array.isArray(res) ? res : res?.notices || [];
        if (!cancelled) {
          const sorted = [...dataList].sort((a, b) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            return new Date(b.createdAt) - new Date(a.createdAt);
          });
          setNotices(sorted);
        }
      } catch (err) {
        console.error("Failed to load notice widget data", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPortal]);

  const basePath = isPortal ? "/staff-panel/notices" : "/notices";

  const recentNotices = notices.slice(0, 3);
  const oldNotices = notices.slice(3);

  return (
    <WidgetCard
      title="Recent Notices"
      icon={Bell}
      loading={loading}
      empty={!loading && notices.length === 0}
      emptyText="No recent notices available."
      onViewAll={() => navigate(basePath)}
    >
      <div className="space-y-4">
        {/* Recent Notices */}
        <div className="space-y-2">
          {recentNotices.map((notice) => (
            <div
              key={notice._id}
              onClick={() => navigate(basePath)}
              className="p-3 rounded-xl border border-slate-200/80 bg-gradient-to-r from-slate-50/50 to-white hover:border-purple-300 hover:shadow-xs transition-all duration-200 cursor-pointer space-y-1.5"
            >
              <div className="flex items-center justify-between gap-2">
                <Badge
                  variant={CATEGORY_VARIANTS[notice.category] || "purple"}
                  className="text-[10px] px-2 py-0.5 font-bold uppercase tracking-wider"
                >
                  {notice.category}
                </Badge>
                <span className="text-[10px] font-semibold text-slate-400 dark:text-neutral-500">
                  {new Date(notice.createdAt).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                  })}
                </span>
              </div>
              <p className="text-xs font-bold text-slate-900 dark:text-neutral-100 line-clamp-1">{notice.title}</p>
              <p className="text-[11px] font-medium text-slate-500 dark:text-neutral-400 line-clamp-1">{notice.content}</p>
            </div>
          ))}
        </div>

        {/* Older Notices Section */}
        {oldNotices.length > 0 && (
          <div className="pt-3 border-t border-slate-100 dark:border-neutral-800 space-y-2">
            <h4 className="text-[11px] font-extrabold text-slate-400 dark:text-neutral-500 uppercase tracking-wider flex items-center gap-1.5">
              <Clock size={13} className="text-slate-400 dark:text-neutral-500" /> Previous Notices
            </h4>
            <div className="space-y-2">
              {oldNotices.map((notice) => (
                <div
                  key={notice._id}
                  onClick={() => navigate(basePath)}
                  className="p-2.5 rounded-xl border border-slate-200/50 bg-neutral-50 dark:bg-neutral-900/60 hover:bg-slate-100/80 transition-all cursor-pointer space-y-1 opacity-80 hover:opacity-100"
                >
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="secondary" className="text-[9px] px-1.5 py-0.2 font-bold uppercase">
                      {notice.category}
                    </Badge>
                    <span className="text-[10px] font-medium text-slate-400 dark:text-neutral-500">
                      {new Date(notice.createdAt).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-slate-800 dark:text-neutral-100 line-clamp-1">{notice.title}</p>
                  <p className="text-[11px] font-normal text-slate-500 dark:text-neutral-400 line-clamp-1">{notice.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </WidgetCard>
  );
};